import React, {
  KeyboardEventHandler,
  KeyboardEvent,
  ReactNode,
  useState
} from 'react'
import Skeleton from 'react-loading-skeleton'

import classnames from 'classnames'
import { v4 as uuidv4 } from 'uuid'

import { normalizeHtmlId } from 'src/shared/js/normalizeUtils'
import { t } from 'src/translations'

import Image from 'shared/components/common/Image'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import { useCart } from 'public/components/online_ordering/CartContext'

const CheckoutSection = ({
  title,
  collapsible = false,
  secondaryHeaderContent,
  className,
  noSectionDivider = false,
  children
}: {
  title?: string
  collapsible?: boolean
  secondaryHeaderContent?: ReactNode
  className?: string
  noSectionDivider?: boolean
  children: ReactNode
}) => {
  const { cart } = useCart()
  const [expanded, setExpanded] = useState(false)
  const id = React.useMemo(() => normalizeHtmlId(uuidv4()), [])

  const handleKeyDown = React.useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case ' ':
        case 'Enter':
          e.stopPropagation()
          e.preventDefault()
          setExpanded(!expanded)
          break
      }
    },
    [expanded]
  )

  if (!cart) {
    return (
      <section className={classnames('checkoutSection', className)}>
        <Skeleton width='100%' height='500px' />
      </section>
    )
  }

  if (collapsible && title) {
    return (
      <>
        <section className={classnames('checkoutSection', className)}>
          <div
            className='checkoutSectionHeader collapsible'
            onClick={() => setExpanded(!expanded)}
            id={`accordionheader${id}`}
            role='button'
            aria-controls={`accordionpanel${id}`}
            aria-expanded={expanded}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <h2 className='checkoutSectionTitle'>{title}</h2>
            <Image
              alt={
                expanded
                  ? t('checkout-section.collapse-section')
                  : t('checkout-section.expand-section')
              }
              className={classnames(
                'controlIcon',
                expanded ? 'rotatedUp' : 'rotatedDown'
              )}
              src='icons/chevron-right-gray.svg'
            />
          </div>
          <div
            id={`accordionpanel${id}`}
            aria-labelledby={`accordionheader${id}`}
            role='region'
          >
            <AnimatedSection
              expanded={expanded}
              testid='checkout-section-content'
            >
              {children}
            </AnimatedSection>
          </div>
        </section>
        {!noSectionDivider && <div className='separator' />}
      </>
    )
  }

  return (
    <>
      <section className={classnames('checkoutSection', className)}>
        <div className='checkoutSectionHeader'>
          {title && (
            <h2 className={classnames('checkoutSectionTitle')}>{title}</h2>
          )}
          {secondaryHeaderContent}
        </div>
        {children}
      </section>
      {!noSectionDivider && <div className='separator' />}
    </>
  )
}

export default CheckoutSection
